import {
  Button,
  Divider,
  Stack,
  Box,
  Typography,
  FormLabel,
  Alert,
  Switch,
  FormGroup,
} from '@mui/material';
import { useForm } from 'react-typed-form';
import {
  FieldText,
  FieldTextRequired,
  FieldRadioGroup,
  FieldSwitch,
  FieldSelect,
  FieldMediaUpload,
} from '../../form';
import { toast } from 'sonner';
import { useNavigate } from 'react-router';
import { Api, useApiCallable } from '../../api';
import { TrashSimple, Info } from '@phosphor-icons/react';
import ConfirmModal from '../../components/ConfirmModal';
import { useState, useEffect } from 'react';

type Props = {
  existing?: Api.User.V_ClinicianDetailAsAdmin | null;
  founderExists?: boolean;
};

const accessLevelOptions = [
  { label: 'Please Select', value: '' },
  { label: 'Editor', value: 'editor' },
  { label: 'Viewer', value: 'viewer' },
];

export default function ClinicianForm({ existing, founderExists }: Props) {
  const navigate = useNavigate();
  const callApi = useApiCallable();
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const isExistingPhysician = existing?.clinicianProfile?.type === 'physician';
  const isExistingFounder = existing?.clinicianProfile?.type === 'founder';
  const hasAssignedUsers = (existing?.totalClients ?? 0) > 0;

  const canUpgradeToFounder =
    isExistingPhysician &&
    (isExistingFounder || !founderExists) &&
    !hasAssignedUsers;
  const showUpgradeToFounder = isExistingPhysician || isExistingFounder;

  const form = useForm<{
    name: string;
    email: string;
    type: string;
    canTakeNewClients: boolean;
    accessLevel: string;
    profilePicture: string | null;
    specialisation: string | null;
    aboutDescription: string | null;
  }>({
    defaultValues: {
      name: existing?.name || '',
      email: existing?.email || '',
      type: existing?.clinicianProfile?.type || 'coach',
      canTakeNewClients: existing?.clinicianProfile?.isActive ?? true,
      accessLevel: existing
        ? existing?.clinicianProfile?.hasEditAccess
          ? 'editor'
          : 'viewer'
        : '',
      profilePicture: existing?.displayPicture?.['@id'] || null,
      specialisation: existing?.clinicianProfile?.specialisation || null,
      aboutDescription: existing?.clinicianProfile?.description || null,
    },
    onSubmit: async (values, form) => {
      const { setLoading, addSubmitError } = form;
      setLoading(true);

      const submitData = {
        name: values.name,
        email: values.email,
        clinicianProfile: {
          type: values.type,
          isActive: values.canTakeNewClients ? true : false,
          hasEditAccess: values.accessLevel === 'editor',
          description: values.aboutDescription || '',
          specialisation: values.specialisation,
        },
        displayPicture: values.profilePicture,
      };

      const response = await callApi(
        existing
          ? `/clinicians/${existing['@id'].split('/').pop()}`
          : '/clinicians',
        {
          method: existing ? 'PUT' : 'POST',
          jsonBody: submitData,
        },
        { addSubmitError },
      );

      setLoading(false);

      if (response) {
        toast.success(existing ? 'Clinician updated' : 'Clinician created');
        navigate('/clinicians');
      }
    },
  });

  const isFounderSelected = form.getField('type').value === 'founder';

  useEffect(() => {
    if (isFounderSelected) {
      form.getField('canTakeNewClients').handleValueChange(true);
    }
  }, [isFounderSelected]);

  return (
    <Stack gap={3}>
      <FieldTextRequired field={form.getField('name')} label="Name" required />

      <FieldTextRequired
        field={form.getField('email')}
        label="Email"
        required
      />

      <Box>
        <FieldRadioGroup
          field={form.getField('type')}
          label="Type"
          options={[
            { label: 'Health Coach', value: 'coach' },
            { label: 'Lifestyle Medicine Physician', value: 'physician' },
          ]}
          required
          disabled={!!existing}
        />

        {showUpgradeToFounder && (
          <FormGroup sx={{ maxWidth: 400, mt: 2 }}>
            <Box
              sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, ml: 1 }}
            >
              <Switch
                checked={isFounderSelected}
                onChange={(_e, checked) =>
                  form
                    .getField('type')
                    .handleValueChange(checked ? 'founder' : 'physician')
                }
                disabled={!canUpgradeToFounder && !isExistingFounder}
                sx={{ mt: -1 }}
              />
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  Upgrade to Founder
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  4 free sessions & unlimited chat access for all app users.
                </Typography>
                {!isExistingFounder && isExistingPhysician && founderExists && (
                  <Alert
                    severity="info"
                    icon={<Info size={20} />}
                    sx={{ mt: 1 }}
                  >
                    A Founder already exists. Only one Founder can be active at
                    a time.
                  </Alert>
                )}
                {!isExistingFounder &&
                  isExistingPhysician &&
                  !founderExists &&
                  hasAssignedUsers && (
                    <Alert
                      severity="info"
                      icon={<Info size={20} />}
                      sx={{ mt: 1 }}
                    >
                      Before upgrading to Founder, all users assigned to this
                      clinician must be reassigned to a different Lifestyle
                      Medicine Physician.
                    </Alert>
                  )}
              </Box>
            </Box>
          </FormGroup>
        )}
      </Box>

      <Box>
        <FormLabel>Can they take on new clients?</FormLabel>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FieldSwitch
            field={form.getField('canTakeNewClients')}
            label=""
            disabled={isFounderSelected}
          />
          <FormLabel>
            {form.getField('canTakeNewClients').value ? 'Yes' : 'No'}
          </FormLabel>
        </Box>
      </Box>

      <FieldSelect
        field={form.getField('accessLevel')}
        label="Access"
        options={accessLevelOptions}
        helperText="Editors have access to User Controls for their assigned users, e.g. enabling them to overwrite content tags"
        required
      />

      <FieldMediaUpload
        field={form.getField('profilePicture')}
        label="Profile Picture"
        acceptedTypes={['IMAGE_TYPE']}
        helperText="1:1 aspect ratio - this will be shown in the app"
        existingAsset={existing?.displayPicture}
        required
      />

      {form.getField('type').value === 'physician' && (
        <FieldText
          field={form.getField('specialisation')}
          label="Specialisation"
          placeholder="Enter physician's area of specialisation"
          required
        />
      )}

      <Stack gap={1}>
        <FormLabel required>About Description</FormLabel>
        <FieldText
          field={form.getField('aboutDescription')}
          label=""
          multiline
          rows={6}
          required
        />
        <Typography variant="caption" color="text.secondary">
          max. 500 characters - this will be shown in the app
        </Typography>
      </Stack>

      <Divider />

      <Stack direction="row" gap={2} justifyContent="space-between">
        <Box>
          <Button variant="contained" onClick={form.handleSubmit}>
            {existing ? 'Save Changes' : 'Create'}
          </Button>
          {!existing && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              When a clinician is created, they will receive an email with
              instructions to set up their password to access the management
              platform
            </Typography>
          )}
        </Box>
        {existing && (
          <Stack alignItems="flex-end" gap={0.5}>
            <Button
              variant="contained"
              color="error"
              onClick={() => setShowDeleteModal(true)}
              startIcon={<TrashSimple />}
              disabled={existing?.totalClients > 0}
            >
              Delete
            </Button>
            <Typography variant="body2" color="text.secondary">
              Clinicians can only be deleted if no users are assigned to them
            </Typography>
          </Stack>
        )}
      </Stack>

      {showDeleteModal && (
        <ConfirmModal
          title="Delete Clinician"
          onClose={() => setShowDeleteModal(false)}
          onConfirm={async () => {
            if (!existing) return;

            const response = await callApi(
              `/clinicians/${existing['@id'].split('/').pop()}`,
              {
                method: 'DELETE',
              },
            );

            if (response) {
              setShowDeleteModal(false);
              toast.success('Clinician deleted');
              navigate('/clinicians');
            }
          }}
          color="error"
          confirmText="Delete"
        >
          Are you sure you want to delete this clinician? This action cannot be
          undone.
        </ConfirmModal>
      )}
    </Stack>
  );
}
